import { deepUpdate } from '../../tools';
import cloneDeep from 'lodash/cloneDeep';
import {getCartFromLocalStorage, isCarDataInCart} from '../../shopping-cart/2023/shopping-cart-utils';

/**
 * Main carPage class. Inspired by React and Redux like pattern of handling the app state in one place,
 * and broadcasting all updates of changes in the app state to dependent observers.
 */
class CarPageApp {
  static carPageInstance = false; // One Singleton to rule them all, and in the context bind them
  #state = {}; // The actual private member that holds the app state.

  /**
   * @constructor
   * @param {string} identifier car's license or identifier
   * @returns {CarPageApp} CarPageApp singleton instance
   */
  constructor(identifier) {

    if (CarPageApp.carPageInstance) {
      // Return singleton reference if it exists already
      return CarPageApp.carPageInstance;
    }

    if( typeof identifier !== 'string' || typeof identifier === 'string' && identifier.length === 0 ) {
      throw TypeError('Invalid identifier supplied, type should be a string and length at least one charapters. Given value was: ' + identifier);
    }

    // Store singleton reference for future instances.
    CarPageApp.carPageInstance = this;
    window.carPageApp = this;

    //Initialize these only once
    this.carId = identifier;
    this.storageKey = `CarPageApp_state_${this.carId}`;
    
    this.#bind(['storeState']);
    
    this.#initialState();
    this.#fetchEnvironment();
    this.#fetchState();
  }

  observers = [];

  // ==== Public Methods ====
  /**
   * Sub components connect to this object, to receive updates of the state
   * @param  {function} callback A method that can receive a state object.
   * @throws {TypeError} if a argument is something else than an function
   */
  registerStateObserver(callback) {
    if (typeof callback === 'function') {
      this.observers.push(callback);
    } else {
      throw new TypeError(`registerStateObserver(argument:function) : Supplied argument should be an function. Given argument type was ${typeof callback}.`);
    }
  }

  /**
   * Public handle for sub components to send and broadcast state updates.
   * @param {string} key dot.notation.address to say which member in the state tree should be updated.
   * @param {*} val anything basically, but stringifiable, because the object is to stringify and stored value to localstorage.
   * @param {boolean} force update whole object in the path
   */
  updateState(key, val, force) {

    // Key should be dot path address.
    // Updating key 'calculatorSettings.fiksudiili.kilometers' should translate to updating object path
    // state.calculatorSettings.fiksudiili.kilometers instead of
    // state['calculatorSettings.fiksudiili.kilometers'] which is a completely different thing.

    this.#state = {
      ...this.#state,
      ...deepUpdate(this.#state, key, val, force)
    };

    this.storeState();
    this.#notifyObservers();
  }

  /**
   * Only way to read the state outside the class is by requesting it thru this handle.
   * @return {object} The actual state object
   */
  getState() {
    return cloneDeep(this.#state);
  }

  /**
   * Reset state object to initial state. Used for testing..
   */
  resetState() {
    this.#initialState();
  }

  /**
   * WIP. This doesn't do anything yet. Hopefully fetches stuff from server and passes it to a callback, when finished.
   * @param  {string} url server URL
   * @param  {object} params request parameters
   * @param  {function} callback called, when data is fetched
   */
  requestServerData(url, params, callback) {
    // Fetch data from backend url with parameters. Store the result to state with key ...
    // Check if the url and params have been fetched already previously, and call callback with that result.
    // else fetch new result from server.
    // For debugging there's a slight problem here, meaning you may want to bypass caching when developing.
    // Need some way to circumvent caching per maybe environment variable... MVP: just fetch shit and store nothing.
  }

  // ==== Private Methods ====

  /**
   * binds the .this context to given method, so they
   * @param  {string[]} methods array of method names that should be binded.
   */
  #bind(methods) {
    methods.map(method => {
      this[method] = this[method].bind(this);
    });
  }

  /**
   * When something changes in the state, this is called to broadcast the new state to observers
   */
  #notifyObservers() {
    const cloneState = cloneDeep(this.#state);
    this.observers.map(obs => obs(cloneState));
  }

  /**
   * Default values for the app state
   */
  #initialState() {
    // Create some sort of default values, picking from page data-attributes etc...
    this.#state = {

      carData: {
        fiksudiiliPrices: [],
      },
      dialogs: {
        contactDialog: false,
        financialDialog: false,
        addCartDialog: false
      },

      //LaakkonenFinancingFormTabs state
      financingFormTabs: {},


      cart: {
        // Is car in the cart or not
        isInCart: false,
      },

      //Osamaksurahoitus settings
      partPaymentCalculatorSettings: {
        financeInfo: ''
      },

      //Fiksu & Greendiili settings
      fiksugreendiiliCalculatorSettings: {
        duration: 48,
        mileage: 40,
      },
    };
  }

  /**
   * Attempt to read state data from local storage, if it exists. If not, or the data can't be parsed, just ignore silently
   */
  #fetchState() {
    try {
      const stateObject = JSON.parse(localStorage.getItem(this.storageKey));

      //Cardata hash has changed from last time.. update data.
      if(stateObject?.carData?.hash !== this.#state?.carData?.hash) {
        stateObject.carData = this.#state.carData;
      }

      this.#state = {
        ...this.#state,
        ...stateObject
      };
    } catch (error) {
      // No biggie, just ignore...
    }
  }

  /**
   * Attempt to store app state to individual localStorage key
   */
  storeState() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.#state));
    } catch (error) {
      // No biggie, just ignore...
    }
  }

  /**
   * Function can be use to set page state trough enviroment related variables like URL, session storage or other sources.
   * This state is overruled by state stored in localStorage.
   */
  async #fetchEnvironment() {

    const carDataJSONElement = document.getElementById('cardata-json');

    if( carDataJSONElement ) {
      this.#state.carData = JSON.parse( carDataJSONElement.textContent );
    }

    const fiksudiiliDuration = sessionStorage.getItem('fiksudiiliDuration');

    if( fiksudiiliDuration ) {
      const [months, mileage] = fiksudiiliDuration.split('_');
      this.#state.fiksugreendiiliCalculatorSettings.mileage = parseInt( mileage );
      this.#state.fiksugreendiiliCalculatorSettings.duration = parseInt( months );
    }

    const cart = getCartFromLocalStorage();
    this.#state.cart.isInCart = isCarDataInCart(this.#state.carData, cart); 
  }
}

export default CarPageApp;
